import api from "@/utils/api";

// Types
export interface ChatMessage {
  id: number;
  uuid: string;
  slate_id: number;
  sender_id: number;
  sender_name: string;
  sender_type: "admin" | "client";
  message: string;
  is_read_admin: boolean;
  is_read_client: boolean;
  created_at: string;
  updated_at: string;
}

export interface DeliveryHubSchool {
  slate_id: number;
  slate_name: string;
  slate_slug?: string;
  city_name?: string;
  area_name?: string;
  area_slug?: string;
  automation?: number;
  notification_contacts_count?: number;
  webhook_count?: number;
  attention_count?: number;
  revision_count?: number;
  query_count?: number;
  unread_comment_count?: number;
}

export interface Deliverable {
  id: number;
  uuid?: string;
  review_token?: string;
  school_id: number;
  slate_name?: string;
  city_name?: string;
  area_name?: string;
  title: string;
  description: string;
  type: "blog" | "video";
  url?: string;
  status: "approved" | "pending" | "needs_revision";
  created_at: string;
  updated_at: string;
  created_by?: number;
  approved_by?: number;
  approved_at?: string;
  reviewer_name?: string;
  reviewed_at?: string;
  delivery_month?: number;
  delivery_year?: number;
  review_links_summary?: {
    total: number;
    approved: number;
    pending: number;
    needs_revision: number;
  };
  comment_count?: number;
  unread_comment_count?: number;
  last_admin_comment?: {
    user_name: string;
    comment: string;
    created_at: string;
  } | null;
  last_client_comment?: {
    user_name: string;
    comment: string;
    created_at: string;
  } | null;
}

/**
 * Get all client schools for DeliveryHub with attention counts
 */
export const getDeliveryHubSchools = async (): Promise<DeliveryHubSchool[]> => {
  try {
    const response = await api.get<{ schools: DeliveryHubSchool[]; total_attention: number }>("/delivery-hub/schools");
    return response?.data?.schools || [];
  } catch (error) {
    console.error("Error fetching DeliveryHub schools:", error);
    return [];
  }
};

export interface AllDeliverablesStats {
  total: number;
  pending: number;
  approved: number;
  needs_revision: number;
}

/**
 * Get all deliverables across all schools (admin overview)
 */
export const getAllDeliverables = async (
  month?: number,
  year?: number
): Promise<{ deliverables: Deliverable[]; stats: AllDeliverablesStats }> => {
  try {
    const params = new URLSearchParams();
    if (month) params.append("month", String(month));
    if (year) params.append("year", String(year));
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      deliverables: Deliverable[];
      stats: AllDeliverablesStats;
    }>(`/delivery-hub/deliverables/all${query}`);
    return {
      deliverables: response?.data?.deliverables || [],
      stats: response?.data?.stats || { total: 0, pending: 0, approved: 0, needs_revision: 0 },
    };
  } catch (error) {
    console.error("Error fetching all deliverables:", error);
    return {
      deliverables: [],
      stats: { total: 0, pending: 0, approved: 0, needs_revision: 0 },
    };
  }
};

/**
 * Get notification contacts for a school
 */
export const getSchoolNotificationContacts = async (schoolId: number): Promise<{ contacts: any[]; count: number }> => {
  try {
    const response = await api.get<{ contacts: any[] }>(`/schools/${schoolId}/notification-contacts`);
    const contacts = response?.data?.contacts || [];
    return { contacts, count: contacts.length };
  } catch (error) {
    console.error("Error fetching notification contacts:", error);
    return { contacts: [], count: 0 };
  }
};

/**
 * Get deliverables for a specific school
 */
export const getSchoolDeliverables = async (
  schoolId: number,
  filters?: { type?: string; status?: string }
): Promise<Deliverable[]> => {
  try {
    const params = new URLSearchParams();
    if (filters?.type) params.append("type", filters.type);
    if (filters?.status) params.append("status", filters.status);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{ deliverables: Deliverable[] }>(`/delivery-hub/schools/${schoolId}/deliverables${query}`);
    return response?.data?.deliverables || [];
  } catch (error) {
    return [];
  }
};

/**
 * Create a new deliverable
 */
export const createDeliverable = async (
  schoolId: number,
  data: { title: string; description?: string; type?: string; url?: string; delivery_month?: number; delivery_year?: number }
): Promise<Deliverable | null> => {
  try {
    const response = await api.post<{ deliverable: Deliverable }>(`/delivery-hub/schools/${schoolId}/deliverables`, data);
    return response?.data?.deliverable || null;
  } catch (error) {
    return null;
  }
};

/**
 * Update a deliverable
 */
export const updateDeliverable = async (
  deliverableId: number,
  data: Partial<Deliverable>
): Promise<Deliverable | null> => {
  try {
    const response = await api.put<{ deliverable: Deliverable }>(`/delivery-hub/deliverables/${deliverableId}`, data);
    return response?.data?.deliverable || null;
  } catch (error) {
    return null;
  }
};

/**
 * Delete a deliverable
 */
export const deleteDeliverable = async (deliverableId: number): Promise<boolean> => {
  try {
    await api.delete(`/delivery-hub/deliverables/${deliverableId}`);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get deliverable counts by status for a school
 */
export const getDeliverableCounts = async (
  schoolId: number
): Promise<{ total: number; pending: number; approved: number; needs_revision: number }> => {
  try {
    const response = await api.get<{ total: number; pending: number; approved: number; needs_revision: number }>(
      `/delivery-hub/schools/${schoolId}/deliverables/counts`
    );
    return response?.data || { total: 0, pending: 0, approved: 0, needs_revision: 0 };
  } catch (error) {
    return { total: 0, pending: 0, approved: 0, needs_revision: 0 };
  }
};

// =============================================
// Comments API
// =============================================

export interface DeliverableComment {
  id: number;
  uuid: string;
  deliverable_id: number;
  user_id: number;
  user_name: string;
  comment: string;
  created_at: string;
}

/**
 * Get all comments for a deliverable
 */
export const getDeliverableComments = async (
  deliverableId: number
): Promise<DeliverableComment[]> => {
  try {
    const response = await api.get<{ comments: DeliverableComment[] }>(
      `/delivery-hub/deliverables/${deliverableId}/comments`
    );
    return response?.data?.comments || [];
  } catch (error) {
    return [];
  }
};

/**
 * Add a comment to a deliverable
 */
export const addDeliverableComment = async (
  deliverableId: number,
  comment: string
): Promise<DeliverableComment | null> => {
  try {
    const response = await api.post<{ comment: DeliverableComment }>(
      `/delivery-hub/deliverables/${deliverableId}/comments`,
      { comment }
    );
    return response?.data?.comment || null;
  } catch (error) {
    return null;
  }
};

/**
 * Delete a comment
 */
export const deleteDeliverableComment = async (
  deliverableId: number,
  commentId: number
): Promise<boolean> => {
  try {
    const response = await api.delete<{ success: boolean }>(`/delivery-hub/deliverables/${deliverableId}/comments/${commentId}`);
    return response?.success ?? true;
  } catch (error) {
    console.error("Error deleting comment:", error);
    return false;
  }
};

// =============================================
// Notification API
// =============================================

export interface NotificationContact {
  id: number;
  slate_id: number;
  name: string;
  designation?: string;
  phone?: string;
  email?: string;
  notify_email: boolean;
  notify_whatsapp: boolean;
  is_active: boolean;
}

export interface NotificationResult {
  contactId: number;
  name: string;
  phone: string;
  success: boolean;
  error?: string;
}

export interface DeliveryhubTemplate {
  id: number;
  template_key: string;
  template_name: string;
  deliverable_type: "blog" | "parent_video" | "educator_video";
  notification_stage: "initial" | "reminder" | "final";
  description: string;
  message_preview: string;
  parameters: string[];
}

/**
 * Get all DeliveryHub WhatsApp templates
 */
export const getDeliveryhubTemplates = async (
  type?: string,
  stage?: string
): Promise<DeliveryhubTemplate[]> => {
  try {
    const params = new URLSearchParams();
    if (type) params.append("type", type);
    if (stage) params.append("stage", stage);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{ templates: DeliveryhubTemplate[] }>(
      `/delivery-hub/templates${query}`
    );
    return response?.data?.templates || [];
  } catch (error) {
    console.error("Error fetching templates:", error);
    return [];
  }
};

/**
 * Send WhatsApp notification to selected contacts
 */
export interface DraftBlogPost {
  id: string;
  title: string;
  slug: string;
  status: string;
  publishedAt?: string;
  excerpt?: string;
  url: string;
}

/**
 * Get draft blog posts from yblog
 * @param schoolId - School ID (currently unused, for future filtering)
 * @param search - Optional search term for autocomplete
 */
export const getDraftBlogPosts = async (
  schoolId: number,
  search?: string
): Promise<{ posts: DraftBlogPost[]; totalPosts: number }> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("limit", "50");
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      posts: DraftBlogPost[];
      totalPosts: number;
    }>(`/delivery-hub/schools/${schoolId}/draft-posts${query}`);
    return {
      posts: response?.data?.posts || [],
      totalPosts: response?.data?.totalPosts || 0,
    };
  } catch (error) {
    console.error("Error fetching draft blog posts:", error);
    return { posts: [], totalPosts: 0 };
  }
};

/**
 * Send WhatsApp notification to selected contacts
 */
export const sendDeliverableNotification = async (
  deliverableId: number,
  contactIds: number[],
  notificationStage: "initial" | "reminder" | "final" = "initial",
  customLink?: string
): Promise<{ success: boolean; results?: NotificationResult[]; message?: string }> => {
  try {
    const response = await api.post<{ results: NotificationResult[] }>(
      `/delivery-hub/deliverables/${deliverableId}/notify`,
      {
        contactIds,
        notificationStage,
        ...(customLink && { link: customLink })
      }
    );
    return {
      success: response?.success ?? true,
      results: response?.data?.results || [],
      message: response?.message,
    };
  } catch (error) {
    return { success: false, message: "Failed to send notifications" };
  }
};

// =============================================
// Review Links API
// =============================================

export interface ReviewLinkContact {
  id: number;
  name: string;
  designation?: string;
  phone?: string;
  email?: string;
}

export interface ReviewLink {
  id: number;
  review_token: string;
  review_url: string;
  contact: ReviewLinkContact | null;
  review_status: "pending" | "approved" | "needs_revision";
  review_comment?: string;
  sent_at?: string;
  viewed_at?: string;
  reviewed_at?: string;
  created_at: string;
}

export interface ReviewLinksSummary {
  total: number;
  approved: number;
  pending: number;
  needs_revision: number;
}

/**
 * Get all review links for a deliverable
 */
export const getDeliverableReviewLinks = async (
  deliverableId: number
): Promise<ReviewLink[]> => {
  try {
    const response = await api.get<{ reviewLinks: ReviewLink[] }>(
      `/delivery-hub/deliverables/${deliverableId}/review-links`
    );
    return response?.data?.reviewLinks || [];
  } catch (error) {
    console.error("Error fetching review links:", error);
    return [];
  }
};

// =============================================
// Blog Publish API
// =============================================

export interface BlogStatus {
  is_blog: boolean;
  has_url?: boolean;
  blog_status?: "draft" | "published" | null;
  blog_title?: string;
  published_at?: string;
  error?: string;
}

/**
 * Get the publish status of associated blog post
 */
export const getBlogStatus = async (
  deliverableId: number
): Promise<BlogStatus> => {
  try {
    const response = await api.get<BlogStatus>(
      `/delivery-hub/deliverables/${deliverableId}/blog-status`
    );
    return response?.data || { is_blog: false };
  } catch (error) {
    console.error("Error fetching blog status:", error);
    return { is_blog: false };
  }
};

/**
 * Publish a blog post from Delivery Hub
 * Requires deliverable to be approved first
 */
export const publishBlog = async (
  deliverableId: number
): Promise<{ success: boolean; message: string; blog_url?: string }> => {
  try {
    const response = await api.post<{ blog_url: string; published_at: string }>(
      `/delivery-hub/deliverables/${deliverableId}/publish-blog`,
      {}
    );
    return {
      success: response?.success ?? true,
      message: response?.message || "Blog published successfully",
      blog_url: response?.data?.blog_url,
    };
  } catch (error: any) {
    console.error("Error publishing blog:", error);
    return {
      success: false,
      message: error?.message || "Failed to publish blog",
    };
  }
};

// =============================================
// Client Queries API (Admin)
// =============================================

export interface QueryAttachment {
  url: string;
  name: string;
  size?: number | null;
  type?: string | null;
}

export interface QueryOwner {
  id: number;
  name: string;
}

export interface QueryCategory {
  id: number;
  name: string;
  owners: QueryOwner[];
}

export interface ClientQuery {
  id: number;
  uuid: string;
  slate_id?: number;
  school_name?: string;
  subject: string;
  message: string;
  status: "open" | "in_progress" | "resolved";
  category?: { id: number; name: string } | null;
  assigned_owner?: QueryOwner | null;
  attachments?: QueryAttachment[];
  days_open?: number | null;
  created_by_name?: string;
  resolved_by_name?: string;
  resolved_at?: string;
  admin_notes?: string;
  reopen_comment?: string;
  reopened_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface ClientQueryStats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
}

/**
 * Get all queries for a school (admin)
 */
export const getSchoolQueries = async (
  schoolId: number,
  status?: string
): Promise<{ queries: ClientQuery[]; stats: ClientQueryStats }> => {
  try {
    const params = new URLSearchParams();
    if (status && status !== "all") params.append("status", status);
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      queries: ClientQuery[];
      stats: ClientQueryStats;
    }>(`/delivery-hub/schools/${schoolId}/queries${query}`);
    return {
      queries: response?.data?.queries || [],
      stats: response?.data?.stats || { total: 0, open: 0, in_progress: 0, resolved: 0 },
    };
  } catch (error) {
    console.error("Error fetching school queries:", error);
    return {
      queries: [],
      stats: { total: 0, open: 0, in_progress: 0, resolved: 0 },
    };
  }
};

/**
 * Create a query (admin-initiated)
 */
export const createAdminQuery = async (
  schoolId: number,
  data: { subject: string; message: string; category_id?: number; attachments?: QueryAttachment[] }
): Promise<{ success: boolean; query?: ClientQuery; message?: string }> => {
  try {
    const response = await api.post<{ query: ClientQuery }>(
      `/delivery-hub/schools/${schoolId}/queries`,
      data
    );
    return {
      success: response?.success ?? true,
      query: response?.data?.query,
      message: response?.message,
    };
  } catch (error) {
    console.error("Error creating query:", error);
    return { success: false, message: "Failed to create query" };
  }
};

/**
 * Update a query (admin)
 */
export const updateQuery = async (
  queryId: number,
  data: { status?: string; admin_notes?: string; category_id?: number; assigned_owner_id?: number }
): Promise<{ success: boolean; query?: ClientQuery; message?: string }> => {
  try {
    const response = await api.put<{ query: ClientQuery }>(
      `/delivery-hub/queries/${queryId}`,
      data
    );
    return {
      success: response?.success ?? true,
      query: response?.data?.query,
      message: response?.message,
    };
  } catch (error: any) {
    console.error("Error updating query:", error);
    return {
      success: false,
      message: error?.message || "Failed to update query",
    };
  }
};

// =============================================
// Query Comments API (Admin)
// =============================================

export interface QueryComment {
  id: number;
  uuid: string;
  user_name: string;
  user_type: "client" | "admin";
  comment: string;
  attachments?: QueryAttachment[];
  created_at: string;
}

/**
 * Get comments for a query (admin)
 */
export const getQueryComments = async (
  queryId: number
): Promise<{ comments: QueryComment[]; count: number }> => {
  try {
    const response = await api.get<{ comments: QueryComment[]; count: number }>(
      `/delivery-hub/queries/${queryId}/comments`
    );
    return {
      comments: response?.data?.comments || [],
      count: response?.data?.count || 0,
    };
  } catch (error) {
    console.error("Error fetching query comments:", error);
    return { comments: [], count: 0 };
  }
};

/**
 * Add a comment to a query (admin)
 */
export const addQueryComment = async (
  queryId: number,
  comment: string,
  attachments: QueryAttachment[] = []
): Promise<{ success: boolean; comment?: QueryComment; message?: string }> => {
  try {
    const response = await api.post<{ comment: QueryComment }>(
      `/delivery-hub/queries/${queryId}/comments`,
      { comment, attachments }
    );
    return {
      success: response?.success ?? true,
      comment: response?.data?.comment,
      message: response?.message,
    };
  } catch (error: any) {
    console.error("Error adding query comment:", error);
    return {
      success: false,
      message: error?.message || "Failed to add comment",
    };
  }
};

// =============================================
// Query routing, history and dashboard (internal)
// =============================================

export interface QueryActivity {
  id: number;
  action: "created" | "assigned" | "status_changed" | "reopened" | "whatsapp_sent" | "whatsapp_failed";
  from_value: string | null;
  to_value: string | null;
  actor_name: string | null;
  metadata?: { field?: string; template?: string; error?: string; test_mode?: boolean } | null;
  created_at: string;
}

export interface QueryCountRow {
  id: number | null;
  name: string;
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
}

export interface QueryDashboardStats {
  totals: { total: number; open: number; in_progress: number; resolved: number; overdue: number };
  ageing_days: number;
  by_category: QueryCountRow[];
  by_owner: QueryCountRow[];
}

export interface QueryFilters {
  status?: string;
  category_id?: string;
  owner_id?: string;
  slate_id?: string;
  from?: string;
  to?: string;
  search?: string;
}

const toQueryString = (filters: QueryFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v && v !== "all") params.append(k, v);
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

export const getQueryCategories = async (): Promise<QueryCategory[]> => {
  try {
    const response = await api.get<{ categories: QueryCategory[] }>("/delivery-hub/query-categories");
    return response?.data?.categories || [];
  } catch (error) {
    console.error("Error fetching query categories:", error);
    return [];
  }
};

export const getQueryOwners = async (): Promise<QueryOwner[]> => {
  try {
    const response = await api.get<{ owners: QueryOwner[] }>("/delivery-hub/query-owners");
    return response?.data?.owners || [];
  } catch (error) {
    console.error("Error fetching query owners:", error);
    return [];
  }
};

export const getQueryActivity = async (queryId: number): Promise<QueryActivity[]> => {
  try {
    const response = await api.get<{ activity: QueryActivity[] }>(`/delivery-hub/queries/${queryId}/activity`);
    return response?.data?.activity || [];
  } catch (error) {
    console.error("Error fetching query activity:", error);
    return [];
  }
};

export const getAllQueries = async (filters: QueryFilters = {}): Promise<ClientQuery[]> => {
  try {
    const response = await api.get<{ queries: ClientQuery[] }>(`/delivery-hub/queries${toQueryString(filters)}`);
    return response?.data?.queries || [];
  } catch (error) {
    console.error("Error fetching queries:", error);
    return [];
  }
};

export const getQueryDashboardStats = async (filters: QueryFilters = {}): Promise<QueryDashboardStats | null> => {
  try {
    const response = await api.get<QueryDashboardStats>(`/delivery-hub/queries/stats${toQueryString(filters)}`);
    return response?.data || null;
  } catch (error) {
    console.error("Error fetching query stats:", error);
    return null;
  }
};

export const uploadAdminQueryAttachments = async (
  files: File[]
): Promise<{ success: boolean; attachments: QueryAttachment[]; message?: string }> => {
  try {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    const response = await api.post<{ attachments: QueryAttachment[] }>("/delivery-hub/queries/attachments", form);
    return { success: response?.success ?? true, attachments: response?.data?.attachments || [] };
  } catch (error: any) {
    console.error("Error uploading attachments:", error);
    return { success: false, attachments: [], message: error?.message || "Failed to upload files" };
  }
};

// ─── Chat API ─────────────────────────────────────────────────────────────────

export const getChatMessages = async (
  schoolId: number,
  page = 1,
  since?: string
): Promise<{ messages: ChatMessage[]; total: number; pages: number }> => {
  try {
    const qs = since ? `since=${encodeURIComponent(since)}` : `page=${page}`;
    const response = await api.get<{ messages: ChatMessage[]; total: number; pages: number }>(
      `/chat/${schoolId}/messages?${qs}`
    );
    return {
      messages: response?.data?.messages || [],
      total: response?.data?.total || 0,
      pages: response?.data?.pages || 1,
    };
  } catch (error) {
    console.error("Error fetching chat messages:", error);
    return { messages: [], total: 0, pages: 1 };
  }
};

export const sendChatMessage = async (
  schoolId: number,
  message: string
): Promise<{ success: boolean; message?: ChatMessage; error?: string }> => {
  try {
    const response = await api.post<{ message: ChatMessage }>(
      `/chat/${schoolId}/messages`,
      { message }
    );
    return {
      success: response?.success ?? true,
      message: response?.data?.message,
    };
  } catch (error: any) {
    console.error("Error sending chat message:", error);
    return { success: false, error: error?.message || "Failed to send message" };
  }
};

export const markChatRead = async (schoolId: number): Promise<void> => {
  try {
    await api.put(`/chat/${schoolId}/mark-read`, {});
  } catch (error) {
    console.error("Error marking chat as read:", error);
  }
};

export const getChatUnreadCounts = async (): Promise<Record<number, number>> => {
  try {
    const response = await api.get<{ counts: Record<number, number> }>("/chat/unread-counts");
    return response?.data?.counts || {};
  } catch (error) {
    console.error("Error fetching chat unread counts:", error);
    return {};
  }
};
